import { useRef, useEffect } from 'react'
import * as THREE from 'three'
/**
 * BgCanvas — full-screen Three.js WebGL background.
 * Features:
 *   • 1 400-particle field with pulsing opacity
 *   • TorusKnot, Icosahedron, Octahedron wireframe shapes
 *   • 14 small floating tetra/octa/icosa/cylinder shapes
 *   • Mouse parallax on the camera
 *   • Scroll-driven camera push-through (shapes spin faster and
 *     camera moves forward as you scroll down the page)
 */
export function BgCanvas() {
  const ref = useRef()
  useEffect(() => {
    const canvas = ref.current
    const R = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
    R.setPixelRatio(Math.min(devicePixelRatio, 2))
    R.setSize(innerWidth, innerHeight)

    const S = new THREE.Scene()
    const C = new THREE.PerspectiveCamera(60, innerWidth / innerHeight, 0.1, 1000)
    C.position.z = 90

    /* particle field */
    const N = 1400, pos = new Float32Array(N * 3)
    for (let i = 0; i < N; i++) {
      pos[i * 3]     = (Math.random() - .5) * 220
      pos[i * 3 + 1] = (Math.random() - .5) * 200
      pos[i * 3 + 2] = (Math.random() - .5) * 110 - 10
    }
    const pG = new THREE.BufferGeometry()
    pG.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    const pM = new THREE.PointsMaterial({ color: 0x00d4ff, size: 0.36, transparent: true, opacity: 0.5 })
    S.add(new THREE.Points(pG, pM))

    /* wireframe helpers */
    const wm = (c, o) => new THREE.MeshBasicMaterial({ color: c, wireframe: true, transparent: true, opacity: o })
    const tk = new THREE.Mesh(new THREE.TorusKnotGeometry(18, 4.5, 90, 14), wm(0x00d4ff, .07)); tk.position.set(64, -18, -40); S.add(tk)
    const ic = new THREE.Mesh(new THREE.IcosahedronGeometry(22, 1), wm(0x7c3aed, .07));           ic.position.set(-70, 32, -55); S.add(ic)
    const oc = new THREE.Mesh(new THREE.OctahedronGeometry(16, 2),  wm(0x00d4ff, .055));           oc.position.set(8, 52, -28);  S.add(oc)

    /* small floating shapes (including lime accent) */
    const fG = [
      new THREE.TetrahedronGeometry(3.5),
      new THREE.OctahedronGeometry(2.8),
      new THREE.IcosahedronGeometry(2.5, 0),
      new THREE.CylinderGeometry(1.5, 1.5, 4, 6),
    ]
    const floaters = Array.from({ length: 14 }, (_, i) => {
      const col = i % 3 === 0 ? 0xc8ff5c : i % 2 === 0 ? 0x00d4ff : 0x7c3aed
      const m   = new THREE.Mesh(fG[i % 4], wm(col, .13))
      m.position.set((Math.random() - .5) * 170, (Math.random() - .5) * 130, (Math.random() - .5) * 60 - 15)
      m.userData = { rx: Math.random() * .008 + .002, ry: Math.random() * .009 + .002, fy: Math.random() * Math.PI * 2, fs: Math.random() * .004 + .002, by: m.position.y }
      S.add(m); return m
    })

    const mouse = { x: 0, y: 0 }; let scrollY = 0, scrollFrac = 0
    const onM = e => { mouse.x = (e.clientX / innerWidth - .5) * 2; mouse.y = -(e.clientY / innerHeight - .5) * 2 }
    const onS = () => { scrollY = window.scrollY; const h = document.documentElement; scrollFrac = scrollY / Math.max(1, h.scrollHeight - h.clientHeight) }
    const onR = () => { C.aspect = innerWidth / innerHeight; C.updateProjectionMatrix(); R.setSize(innerWidth, innerHeight) }
    window.addEventListener('mousemove', onM)
    window.addEventListener('scroll', onS, { passive: true })
    window.addEventListener('resize', onR)

    let t = 0, aid
    const animate = () => {
      aid = requestAnimationFrame(animate); t += .005
      const spin = 1 + scrollFrac * 2.4
      tk.rotation.x += .0028 * spin; tk.rotation.y += .0018 * spin
      ic.rotation.x -= .002  * spin; ic.rotation.y += .0038 * spin
      oc.rotation.z += .0028 * spin; oc.rotation.x += .0012 * spin
      pM.opacity = .42 + Math.sin(t) * .09
      floaters.forEach(f => { f.rotation.x += f.userData.rx; f.rotation.y += f.userData.ry; f.userData.fy += f.userData.fs; f.position.y = f.userData.by + Math.sin(f.userData.fy) * 3 })
      const targetZ = 90 - scrollFrac * 55
      C.position.z += (targetZ - C.position.z) * .04
      C.position.x += (mouse.x * 9 - C.position.x) * .018
      C.position.y += (mouse.y * 6 + scrollY * .005 - C.position.y) * .018
      C.lookAt(S.position); R.render(S, C)
    }
    animate()
    return () => {
      cancelAnimationFrame(aid)
      window.removeEventListener('mousemove', onM)
      window.removeEventListener('scroll', onS)
      window.removeEventListener('resize', onR)
      R.dispose()
    }
  }, [])
  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
}
